// apps/web/src/features/commercial-proposals/components/CommercialProposalTariffUnitControl.tsx
//
// @aiPurpose the 2-way tariff-unit selector (#1411): per kg / per m³, bound
//   to the editor form's `tariffUnit`. Mirrors CommercialProposalModeControl.
import { useTranslation } from 'react-i18next';
import {
  COMMERCIAL_PROPOSAL_TARIFF_UNITS,
  type CommercialProposalTariffUnit,
} from '@magimp/shared';

// Static slug + i18n-key map keeps the testid + translation key as string
// LITERALS (i18next types reject a widened `string` key).
const UNIT_META: Record<
  CommercialProposalTariffUnit,
  { slug: string; labelKey: 'editor.tariffUnit.kg' | 'editor.tariffUnit.m3' }
> = {
  kg: { slug: 'kg', labelKey: 'editor.tariffUnit.kg' },
  m3: { slug: 'm3', labelKey: 'editor.tariffUnit.m3' },
};

export function CommercialProposalTariffUnitControl({
  value,
  onChange,
}: {
  value: CommercialProposalTariffUnit;
  onChange: (v: CommercialProposalTariffUnit) => void;
}) {
  const { t } = useTranslation('commercialProposals');
  return (
    <div role="radiogroup" style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
      {COMMERCIAL_PROPOSAL_TARIFF_UNITS.map((unit) => {
        const selected = unit === value;
        const meta = UNIT_META[unit];
        return (
          <button
            key={unit}
            type="button"
            role="radio"
            aria-checked={selected}
            data-testid={`commercial-proposals-tariff-unit-${meta.slug}`}
            onClick={() => onChange(unit)}
            style={{
              flex: '1 1 30%',
              minWidth: 120,
              padding: '8px 10px',
              borderRadius: 'var(--r-2)',
              border: `1px solid ${selected ? 'var(--accent-1)' : 'var(--border-1)'}`,
              background: selected ? 'var(--accent-ring)' : 'var(--bg-1)',
              color: 'var(--fg-1)',
              fontSize: 12,
              fontWeight: selected ? 600 : 400,
              cursor: 'pointer',
              textAlign: 'left',
            }}
          >
            {t(meta.labelKey)}
          </button>
        );
      })}
    </div>
  );
}
